/**
 * ENG-5516: per-agent token-usage reporting.
 *
 * For each Claude Code agent, enumerate every session transcript in the
 * agent's project dir (~/.claude/projects/<encoded-cwd>/*.jsonl), parse the
 * cumulative per-(session, model) token totals, and POST them to
 * /host/token-usage where they're upserted. Wired into the per-agent
 * processing loop in manager-worker.ts alongside maybeReportUsageBanner;
 * Claude Code framework only.
 *
 * Why enumerate ALL transcripts (not just the current session):
 *   A day produces multiple transcript files per agent — same-day respawns
 *   resume one pinned UUID (ENG-6039) but UUID rotations (failure recovery,
 *   missing transcript), the AGT_DISABLE_SESSION_RESUME path, scheduled
 *   tasks, and direct-chat each mint their own. They all land in the same
 *   project dir. Tracking only daily-session.json's current.sessionId would
 *   miss every completed-session tail and every ephemeral session.
 *   Enumerating the directory captures all of them.
 *
 * Why cumulative upsert (not byte-offset deltas):
 *   Each transcript is re-read in full and its CUMULATIVE totals posted. The
 *   API upserts on (agent_id, session_id, model), so re-posting an unchanged
 *   session is a no-op and a manager restart can't double-count or lose
 *   tokens. To avoid re-reading unchanged files every tick, an in-memory
 *   {mtimeMs, size} fingerprint per file gates the read — a restart simply
 *   re-reads once (the upsert absorbs it).
 *
 * Errors are swallowed and reported to `log`: this is a soft observability
 * path and must never break the manager poll cycle.
 */

import { readdirSync, readFileSync, statSync } from 'node:fs';
import { join } from 'node:path';
import { isEmptyTotals, parseTranscriptUsage, attributeTranscriptUsageByRun } from '@augmented/core';
import { sessionTranscriptDir } from './daily-session.js';
import { getProjectDir } from './persistent-session.js';

const MIN_CHECK_INTERVAL_MS = 60_000;
// Only consider transcripts touched within this window. Older sessions were
// already upserted and won't change; skipping them bounds per-tick I/O and
// keeps a manager restart's re-read pass small.
const TRANSCRIPT_MTIME_WINDOW_MS = 2 * 24 * 60 * 60 * 1000; // 2 days
// Endpoint caps entries per POST (MAX_TOKEN_USAGE_ENTRIES = 500); stay under.
const MAX_ENTRIES_PER_POST = 200;

interface FileFingerprint {
  mtimeMs: number;
  size: number;
}

interface MonitorState {
  /** sessionId (filename minus .jsonl) → fingerprint of the last POSTed read. */
  files: Map<string, FileFingerprint>;
  /** Last time we enumerated the transcript dir for this code_name. */
  lastCheckedAt: number;
}

const state = new Map<string, MonitorState>();

/** Test helper — reset module state. Not for production use. */
export function _resetTokenUsageMonitorState(): void {
  state.clear();
}

/** Minimal API surface — lets tests stub without the real transport. */
export interface TokenUsageMonitorApi {
  post(path: string, body: unknown): Promise<unknown>;
}

interface TokenUsageEntry {
  session_id: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  session_started_at: string | null;
  last_observed_at: string | null;
}

/**
 * Enumerate the agent's transcripts, build entries for files that changed
 * since we last reported them, POST in batches, and update fingerprints only
 * for files whose POST succeeded.
 */
export async function maybeReportTokenUsage(args: {
  api: TokenUsageMonitorApi;
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  now?: Date;
  /** Test seam: override the transcript directory. */
  transcriptDir?: string;
}): Promise<void> {
  const { api, codeName, agentId, log } = args;
  const now = args.now ?? new Date();
  const nowMs = now.getTime();

  const existing = state.get(codeName);
  if (existing && nowMs - existing.lastCheckedAt < MIN_CHECK_INTERVAL_MS) {
    return;
  }

  const dir = args.transcriptDir ?? sessionTranscriptDir(getProjectDir(codeName));
  const files = existing?.files ?? new Map<string, FileFingerprint>();
  const next: MonitorState = { files, lastCheckedAt: nowMs };

  let dirEntries: string[];
  try {
    dirEntries = readdirSync(dir);
  } catch {
    // No transcript dir yet (agent hasn't run) — record the check and move on.
    state.set(codeName, next);
    return;
  }

  // Collect entries to POST, plus the fingerprint to commit on success.
  const pending: { entry: TokenUsageEntry; sessionId: string; fp: FileFingerprint }[] = [];
  // ENG-5567: per-run attribution rows derived from the SAME transcript reads,
  // POSTed best-effort to /host/run-token-usage. Independent of the token-usage
  // fingerprint commit below (run_token_usage is cumulative + idempotent, so a
  // failed run POST self-heals on the next change).
  const pendingRuns: {
    run_id: string;
    model: string;
    input_tokens: number;
    output_tokens: number;
    cache_creation_tokens: number;
    cache_read_tokens: number;
    last_observed_at: string | null;
  }[] = [];

  for (const name of dirEntries) {
    if (!name.endsWith('.jsonl')) continue;
    const sessionId = name.slice(0, -'.jsonl'.length);
    if (!sessionId) continue;

    const path = join(dir, name);
    let st;
    try {
      st = statSync(path);
    } catch {
      continue; // raced deletion / permission — skip
    }
    if (!st.isFile()) continue;
    if (nowMs - st.mtimeMs > TRANSCRIPT_MTIME_WINDOW_MS) continue; // stale, already reported

    const fp: FileFingerprint = { mtimeMs: st.mtimeMs, size: st.size };
    const seen = files.get(sessionId);
    if (seen && seen.mtimeMs === fp.mtimeMs && seen.size === fp.size) {
      continue; // unchanged since last successful POST
    }

    let content: string;
    try {
      content = readFileSync(path, 'utf-8');
    } catch (err) {
      log(`[token-usage] read failed for '${codeName}/${name}': ${(err as Error).message}`);
      continue;
    }

    const parsed = parseTranscriptUsage(content);
    for (const [model, totals] of parsed.byModel) {
      if (isEmptyTotals(totals)) continue;
      pending.push({
        sessionId,
        fp,
        entry: {
          session_id: sessionId,
          model,
          input_tokens: totals.inputTokens,
          output_tokens: totals.outputTokens,
          cache_creation_tokens: totals.cacheCreationTokens,
          cache_read_tokens: totals.cacheReadTokens,
          session_started_at: parsed.sessionStartedAt,
          last_observed_at: parsed.lastObservedAt,
        },
      });
    }
    // ENG-5567: per-run attribution from the same content. Only runs with a
    // marker (runId !== null) and non-empty totals are reported; the
    // unattributed bucket stays in the session-level token_usage total.
    for (const r of attributeTranscriptUsageByRun(content).perRunModel) {
      if (r.runId === null || isEmptyTotals(r.totals)) continue;
      pendingRuns.push({
        run_id: r.runId,
        model: r.model,
        input_tokens: r.totals.inputTokens,
        output_tokens: r.totals.outputTokens,
        cache_creation_tokens: r.totals.cacheCreationTokens,
        cache_read_tokens: r.totals.cacheReadTokens,
        last_observed_at: parsed.lastObservedAt,
      });
    }

    // Even when a changed file yields no token rows (e.g. only user turns so
    // far), advance its fingerprint so we don't re-read it every tick until
    // it next changes.
    if (![...parsed.byModel.values()].some((t) => !isEmptyTotals(t))) {
      files.set(sessionId, fp);
    }
  }

  if (pending.length === 0) {
    state.set(codeName, next);
    return;
  }

  // POST in batches; commit fingerprints only for sessions in batches that
  // succeeded, so a failure retries those sessions next tick.
  let reported = 0;
  for (let i = 0; i < pending.length; i += MAX_ENTRIES_PER_POST) {
    const batch = pending.slice(i, i + MAX_ENTRIES_PER_POST);
    try {
      await api.post('/host/token-usage', {
        agent_id: agentId,
        framework: 'claude-code',
        source: 'transcript_jsonl',
        entries: batch.map((b) => b.entry),
      });
      for (const b of batch) {
        files.set(b.sessionId, b.fp);
      }
      reported += batch.length;
    } catch (err) {
      log(`[token-usage] POST failed for '${codeName}': ${(err as Error).message}`);
      // Leave fingerprints unset for this batch's sessions → retry next tick.
    }
  }

  // ENG-5567: best-effort per-run usage POST — independent of the token-usage
  // fingerprint commit above (run_token_usage is cumulative + idempotent, so a
  // failed POST self-heals on the next transcript change). Same batch size.
  for (let i = 0; i < pendingRuns.length; i += MAX_ENTRIES_PER_POST) {
    const batch = pendingRuns.slice(i, i + MAX_ENTRIES_PER_POST);
    try {
      await api.post('/host/run-token-usage', {
        agent_id: agentId,
        framework: 'claude-code',
        source: 'transcript_jsonl',
        entries: batch,
      });
    } catch (err) {
      log(`[token-usage] run-usage POST failed for '${codeName}': ${(err as Error).message}`);
    }
  }

  // ENG-5533: emit one success line per flush so a rollout is observable in
  // manager.log without DB access. After the ENG-5516 ship this path stayed
  // silent for 24h because /host/agents never sent `framework`, so the
  // caller's `framework === 'claude-code'` guard meant we never reached here
  // — and since every error path also logs, the *absence* of any
  // [token-usage] line was the only (easily-missed) symptom. Log only when we
  // actually POSTed, not on the every-tick no-op return, to stay quiet.
  if (reported > 0) {
    log(`[token-usage] reported ${reported} entr${reported === 1 ? 'y' : 'ies'} for '${codeName}'`);
  }

  state.set(codeName, next);
}
